import React, { useState } from 'react';
import { X, Send, AlertCircle } from 'lucide-react';
import api from '../services/api';
import { formatPrice } from '../utils/formatters';

const MakeOfferModal = ({ listing, onClose, onOfferSent }) => {
  const [amount, setAmount] = useState(listing?.pro_price || '');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const vehicle = listing?.vehicle || {};
  const askingPrice = listing?.pro_price;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || amount <= 0) {
      setError('Veuillez entrer un montant valide');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post('/marketplace/offers', {
        listing_id: listing.id,
        amount: parseInt(amount),
        message: message || null,
      });
      if (onOfferSent) onOfferSent();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi de l\'offre');
    } finally {
      setLoading(false);
    }
  };

  const percentage = askingPrice && amount ? Math.round((amount / askingPrice) * 100) : null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-dark-card w-full sm:w-[440px] sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-dark-border">
          <h2 className="text-lg font-bold text-primary">Faire une offre</h2>
          <button onClick={onClose} className="p-1 hover:bg-dark-hover rounded-full transition-colors">
            <X className="h-5 w-5 text-muted" />
          </button>
        </div>

        {/* Vehicle summary */}
        <div className="px-5 py-4 bg-dark-hover flex items-center gap-3">
          {vehicle.images?.[0] && (
            <img src={vehicle.images[0]} alt="" className="w-16 h-12 rounded-lg object-cover" />
          )}
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-primary truncate">{vehicle.make} {vehicle.model}</p>
            <p className="text-sm text-muted">{vehicle.year} {vehicle.mileage ? `• ${new Intl.NumberFormat('fr-BE').format(vehicle.mileage)} km` : ''}</p>
          </div>
          {askingPrice && (
            <div className="text-right flex-shrink-0">
              <p className="text-sm text-muted">Prix demandé</p>
              <p className="font-bold text-primary">{formatPrice(askingPrice)}</p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Amount input */}
          <div>
            <label className="block text-sm font-medium text-label mb-2">Votre offre</label>
            <div className="relative">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Montant en EUR"
                className="w-full pl-4 pr-12 py-3 text-xl font-bold bg-dark-input border-2 border-dark-border text-primary rounded-xl focus:border-carmin focus:ring-0 transition-colors placeholder-muted"
                min="1"
                autoFocus
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted font-medium">€</span>
            </div>
            {percentage && percentage !== 100 && (
              <p className={`text-xs mt-1.5 ${percentage < 100 ? 'text-amber-400' : 'text-green-400'}`}>
                {percentage < 100
                  ? `${100 - percentage}% en dessous du prix demandé`
                  : `${percentage - 100}% au-dessus du prix demandé`
                }
              </p>
            )}
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-label mb-2">
              Message <span className="text-muted font-normal">(optionnel)</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Ex: Véhicule disponible immédiatement ? Intéressé pour reprise..."
              rows={2}
              maxLength={500}
              className="w-full px-4 py-3 bg-dark-input border-2 border-dark-border text-primary rounded-xl focus:border-carmin focus:ring-0 resize-none text-sm transition-colors placeholder-muted"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 p-3 rounded-lg border border-red-900/30">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Info */}
          <p className="text-xs text-muted">
            Le vendeur pourra accepter, refuser ou faire une contre-offre. Vous pouvez annuler votre offre tant qu'elle est en attente.
          </p>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !amount}
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-carmin text-white rounded-xl font-semibold hover:bg-carmin-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Send className="h-4 w-4" />
                Envoyer l'offre
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default MakeOfferModal;
